<template>
  <div
    ref="screenShareComponent"
    class="relative group pr-8"
    :class="{ 'pointer-events-none opacity-80': sessionStore.isPhasePaused }"
  >
    <div
      class="
        absolute
        left-0
        top-0
        w-20
        rounded-full
        h-full
        bg-white
        flex
        items-center
        justify-end
        pr-1
        bg-strong-grey
        cursor-pointer
      "
      style="color: #d4d4d4"
      v-if="enableScreenshareOptions"
      @click="showShareOptions = !showShareOptions"
    >
      <ChevronUpIcon class="w-6 h-6 transform" v-if="showShareOptions" aria-hidden="true" />
      <ChevronDownIcon v-else class="w-6 h-6 transform" aria-hidden="true" />
    </div>

    <div
      v-if="showShareOptions"
      class="
        absolute
        left-6
        bottom-full
        bg-white
        divide-y divide-gray-100
        rounded-lg
        shadow
        w-52
        mb-4
      "
      style="bottom: 40px; z-index: 25"
    >
      <ul class="text-left py-2 text-sm text-gray-700">
        <li @click="shareFullscreen">
          <span
            v-if="sessionStore.screenShares[1].screenshareOngoing"
            class="cursor-pointer block px-4 py-2 hover:bg-gray-100"
          >
            Stop sharing screen
          </span>
          <span v-else class="cursor-pointer block px-4 py-2 hover:bg-gray-100">Share screen</span>
        </li>
        <!--        <li @click="shareDeviceScreen">-->
        <!--          <span-->
        <!--            v-if="sessionStore.screenShares[3].screenshareOngoing"-->
        <!--            class="cursor-pointer block px-4 py-2 hover:bg-gray-100"-->
        <!--          >-->
        <!--            Stop sharing device screen-->
        <!--          </span>-->
        <!--          <span v-else class="cursor-pointer block px-4 py-2 hover:bg-gray-100">-->
        <!--            Share device screen-->
        <!--          </span>-->
        <!--        </li>-->
        <li
          @click="shareCroppedScreen"
          v-if="
            !(
              sessionStore.screenShares[1].screenshareOngoing &&
              sessionStore.screenShares[2].screenshareOngoing
            )
          "
        >
          <span class="cursor-pointer block px-4 py-2 hover:bg-gray-100">Share cropped screen</span>
        </li>
        <li @click="secondScreenShare">
          <span
            v-if="sessionStore.screenShares[2].screenshareOngoing"
            class="cursor-pointer block px-4 py-2 hover:bg-gray-100"
          >
            Stop sharing the second Screen
          </span>
          <span v-else class="cursor-pointer block px-4 py-2 hover:bg-gray-100">
            Share a second screen
          </span>
        </li>
      </ul>
    </div>
    <DeviceBadge
      :data-cy="`screenshare-${sessionStore.screenShares[1].screenshareOngoing}`"
      @click="handleScreenShareClick"
      :btnDescription="
        sessionStore.screenShares[1].screenshareOngoing ? 'Stop screensharing' : 'Share your screen'
      "
      :isActive="
        sessionStore.screenShares[1].screenshareOngoing ||
        sessionStore.screenShares[2].screenshareOngoing ||
        sessionStore.screenShares[3].screenshareOngoing
      "
      icon="Screen"
    ></DeviceBadge>
  </div>
  <!-- Video Cropping Modal -->
  <VideoCroppingModal
    :open="Boolean(screensharePreviewImage)"
    :screenShareImage="screensharePreviewImage"
    @share-screen-to-participants="postProcessScreenshare"
    :stopScreenshareStream="stopScreenshareStream"
  />
</template>

<script>
import { computed, onBeforeUnmount, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/solid';
import {
  ENABLE_PARTIAL_SCREENSHARE,
  VICE_SESSION_PHASES,
  DEVICE_SCREEN_SHARE_APP_URL,
} from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';
import useJitsiScreenshare from '@/Composables/useJitsiScreenshare';
import ScreensharePreprocessor from '@/Services/ScreensharePreprocessor';
import DeviceBadge from '@/Components/Badges/DeviceBadge.vue';
import VideoCroppingModal from '@/Components/Modals/VideoCroppingModal.vue';

const { toggleScreenshare: secondToggleScreenShare } = useJitsiScreenshare(2);
const shareDeviceScreenInstance = useJitsiScreenshare(3);

export default {
  components: {
    ChevronDownIcon,
    DeviceBadge,
    ChevronUpIcon,
    VideoCroppingModal,
  },
  setup() {
    const showShareOptions = ref(false);
    const screenShareComponent = ref(false);

    const sessionStore = useSessionStore();
    const { toggleScreenshare, screenShareImage, postProcessScreenshare, stopScreenshareStream } =
      useJitsiScreenshare(1, ENABLE_PARTIAL_SCREENSHARE);
    const screensharePreviewImage = computed(() => screenShareImage.value);
    const isCropScreenSupported = new ScreensharePreprocessor().isMediaProcessingSupported();

    const enableScreenshareOptions = computed(
      () => sessionStore.currentPhase == VICE_SESSION_PHASES.CONSULTATION && isCropScreenSupported,
    );

    const handleScreenShareClick = () => {
      if (enableScreenshareOptions.value) {
        showShareOptions.value = !showShareOptions.value;
      } else if (sessionStore.screenShares[1].screenshareOngoing) {
        sessionStore.toggleControlPanel(true);
        toggleScreenshare();
        showShareOptions.value = false;
      } else {
        sessionStore.toggleControlPanel(true);
        toggleScreenshare();
      }
    };

    const shareFullscreen = () => {
      sessionStore.toggleControlPanel(true);
      toggleScreenshare();
      showShareOptions.value = !showShareOptions.value;
    };

    const shareCroppedScreen = () => {
      sessionStore.toggleControlPanel(true);
      toggleScreenshare(true);
      showShareOptions.value = !showShareOptions.value;
    };

    const shareDeviceScreen = () => {
      if (!sessionStore.screenShares[3].screenshareOngoing) {
        setTimeout(() => {
          window.open(DEVICE_SCREEN_SHARE_APP_URL, '_blank');
        });
      }
      shareDeviceScreenInstance.toggleScreenshare();
      showShareOptions.value = !showShareOptions.value;
    };

    const shareScreenButtonClickHandler = () => {
      showShareOptions.value = !showShareOptions.value;
    };

    onClickOutside(screenShareComponent, () => {
      showShareOptions.value = false;
    });

    onBeforeUnmount(() => {
      setTimeout(() => {
        stopScreenshareStream();
      }, 1000);
    });

    const secondScreenShare = () => {
      secondToggleScreenShare();
      showShareOptions.value = !showShareOptions.value;
    };

    return {
      sessionStore,
      showShareOptions,
      toggleScreenshare,
      screenShareComponent,
      stopScreenshareStream,
      postProcessScreenshare,
      screensharePreviewImage,
      enableScreenshareOptions,
      shareFullscreen,
      shareDeviceScreen,
      secondScreenShare,
      shareCroppedScreen,
      handleScreenShareClick,
      shareScreenButtonClickHandler,
    };
  },
};
</script>
